import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useEffect, useCallback } from 'react';
import { isEqualPath, isActiveLink, isExternalLink } from 'minimal-shared/utils';

import { usePathname } from 'src/routes/hooks';

import { NavItem } from './nav-desktop-item';
import { Nav, NavLi, NavUl, NavDropdown } from '../components';
import { NavItemDashboard } from './nav-desktop-item-dashboard';

// ----------------------------------------------------------------------

export type NavMainSubItem = {
  title: string;
  path: string;
  icon?: React.ReactNode;
};

export type NavMainSubList = {
  subheader?: string;
  items: NavMainSubItem[];
};

export type NavMainItem = NavMainSubItem & {
  children?: NavMainSubList[];
  useDemoLogin?: boolean;
};

type NavListProps = React.ComponentProps<typeof NavLi> & {
  data: NavMainItem;
  onDemoExploreClick?: () => void;
};

export function NavList({ data, sx, onDemoExploreClick, ...other }: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef(null);

  const isActive = isActiveLink(pathname, data.path, !!data.children);
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.children) {
      onOpen();
    }
  }, [data.children, onOpen]);

  const handleNavClick = useCallback(
    (e: React.MouseEvent) => {
      if (data.useDemoLogin) {
        e.preventDefault();
        onDemoExploreClick?.();
      }
    },
    [data.useDemoLogin, onDemoExploreClick]
  );

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      title={data.title}
      // state
      open={open}
      active={isActive}
      // options
      hasChild={!!data.children}
      externalLink={isExternalLink(data.path)}
      // action
      onMouseEnter={handleOpenMenu}
      onMouseLeave={onClose}
      onClick={data.useDemoLogin ? handleNavClick : undefined}
    />
  );

  const renderDropdown = () =>
    !!data.children && (
      <NavDropdown open={open} onMouseEnter={handleOpenMenu} onMouseLeave={onClose}>
        <Nav>
          <NavUl sx={{ gap: 3, flexDirection: 'column' }}>
            {data.children.map((list: NavMainSubList) => (
              <NavSubList key={list.subheader} subheader={list.subheader} data={list.items} />
            ))}
          </NavUl>
        </Nav>
      </NavDropdown>
    );

  return (
    <NavLi sx={{ position: 'relative', ...sx }} {...other}>
      {renderNavItem()}
      {renderDropdown()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

type NavSubListProps = React.ComponentProps<typeof NavLi> & {
  data: NavMainSubItem[];
  subheader?: string;
};

function NavSubList({ data, subheader, sx, ...other }: NavSubListProps) {
  const pathname = usePathname();

  const isDashboard = subheader === 'Dashboard';

  return (
    <NavLi
      sx={[
        () => ({
          flexGrow: 1,
          flexBasis: 'auto',
          flexShrink: isDashboard ? 1 : 0,
          ...(isDashboard && { maxWidth: 560 }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <NavUl sx={{ gap: 2 }}>
        {/* <NavLi
          sx={(theme) => ({
            mb: 1,
            typography: 'overline',
            fontSize: theme.typography.pxToRem(11),
          })}
        >
          {subheader}
        </NavLi> */}

        {data.map((item: NavMainSubItem) =>
          isDashboard ? (
            <NavLi key={item.title}>
              <NavItemDashboard path={item.path} />
            </NavLi>
          ) : (
            <NavLi key={item.title}>
              <NavItem
                subItem
                title={item.title}
                icon={item.icon}
                path={item.path}
                active={isEqualPath(item.path, pathname)}
              />
            </NavLi>
          )
        )}
      </NavUl>
    </NavLi>
  );
}
